'use client'

import {useParams, useRouter , useSearchParams} from '@/utils/navigation'
import {useRef , useEffect, useState} from 'react'
import Heading from '@/components/Heading/Heading'
import {Button, Checkbox} from '@/components/UiElements'
import Popover from '@/components/UiElements/PopOver'
import Modal from "@/components/Modal/Modal";
import LineItemProgressHistoryDialog from './LineItemProgressHistoryDialog'
import OrderHeaderBadge from './OrderHeaderBadge'
import OrderHistoryDialog from './OrderHistoryDialog'
import DesignElementImage from './DesignElementImage'
import SectionImageComparison from './SectionImageComparison'
import type {
    MachineVisualizationProduct,
    MachineVisualizationSection,
} from './MachineImage'
import ImageComponent from '@/components/UiElements/ImageComponent'
// @ts-ignore
import Order3DPlugin from "@/components/Order/Order3DPlugin/Order3DPlugin";
import getOrder from '@/services/OrderService/getOrder'
import updateOrderProfanityCheckLineItems from '@/services/OrderService/updateOrderProfanityCheckLineItems'
import {OrderProfanityCheckStatus, SingleOrder, SingleOrderDetail, OrderJob, SingleOrderSection} from '@/types/order'
import reprocessJob from '@/services/OrderService/reprocessJob'
import reprocessJobLineItems from '@/services/OrderService/reprocessJobLineItems'
import getResultReprocessJobLineItems from '@/services/OrderService/getResultReprocessJobLineItems'
import getResultImageServiceJob from '@/services/OrderService/getResultImageServiceJob'
import getVisualization from '@/services/OrderService/getVisualization'
import markOrderAsDone from '@/services/OrderService/markAsDone'
import {formatDateFromStringWithHour} from '@/utils/formatDate'
import {getStatus} from '@/utils/getStatusOrder'
import getProduct from '@/services/ProductService/getProduct';
import store from '@/store';
import {toast} from 'react-toastify';
import notifyError from '@/utils/notifyError';
import {useSnapshot} from "valtio";
import getSettings from '@/services/ThemeService/getSettings';

import JSZip from 'jszip';
// @ts-ignore
import { saveAs } from 'file-saver';
import markAsDone from '@/services/OrderService/markAsDone';
import { CheckCheck, CheckCircle2, Clock, Copy, Loader2, Printer, RefreshCw, XCircle } from 'lucide-react'; // Loader for visual feedback

interface IdsObject {
    [key: string]: { status: string };
}

// Builds a user-facing message for a failed reprocess/job result. The backend
// sometimes reports is_successful=false with an empty `error`, which would
// otherwise render an empty red toast. Fall back to the failed line-item SKUs,
// then a generic message, so the toast is never blank.
const buildJobFailureMessage = (data: any): string => {
    if (data?.error) return data.error
    const failedSkus: string[] = (data?.line_item_result ?? [])
        .filter((item: any) => item && item.is_successful === false)
        .map((item: any) => item?.sku)
        .filter(Boolean)
    if (failedSkus.length) {
        return `Reprocessing failed for: ${failedSkus.join(', ')}. Please try again.`
    }
    return 'Reprocessing failed. Please try again.'
}

interface OrderProps {
  order?: {
    order_number: string;
  };
}

const isLineItemProfanityRejected = (lineItem: SingleOrderDetail) => (
    String(lineItem.profanity_check_status || '').trim().toLowerCase() === OrderProfanityCheckStatus.REJECTED
)

const isLineItemProfanityApproved = (lineItem: SingleOrderDetail) => (
    String(lineItem.profanity_check_status || '').trim().toLowerCase() === OrderProfanityCheckStatus.ACCEPTED
)

// Customers who skip the customizer supply one multi-page PDF for the whole line item
// instead of a file per section. The pages follow the product's own view order — page 1
// is Front, page 2 is Back — and that order holds whether or not the line item actually
// engraves both: a back-only item still gets a PDF whose first page is the blank front
// view. So the page is chosen by section name, never by the section's position in the
// list, which would send a lone Back section to the empty page 1.
//
// Names we don't recognise fall back to position, and ImageComponent clamps the result
// to the document's page count — so a single-page PDF always lands on page 1 no matter
// which section asks for it. Non-PDF raw images ignore the page entirely.
const SECTION_PDF_PAGES: Record<string, number> = {
    front: 1,
    back: 2,
}

const getSectionPageNumbers = (sections: SingleOrderSection[]) => {
    const countPerBundle = new Map<string, number>()
    return sections.map((section) => {
        const key = String(section.bundle_index ?? '')
        const position = (countPerBundle.get(key) ?? 0) + 1
        countPerBundle.set(key, position)

        const namedPage = SECTION_PDF_PAGES[String(section.name ?? '').trim().toLowerCase()]
        return namedPage ?? position
    })
}

const getMachineVisualizationForSection = (
    lineItem: SingleOrderDetail,
    section: SingleOrderSection
) => {
    const visualizations = (
        Array.isArray(lineItem.visualization) ? lineItem.visualization : []
    ) as MachineVisualizationProduct[]
    const machineProduct = lineItem.is_bundle
        ? visualizations.find((visualization) => (
            String(visualization?.bundle_index) === String(section.bundle_index) &&
            String(visualization?.sku) === String(section.product_sku)
        ))
        : visualizations.find((visualization) => (
            String(visualization?.sku) === String(section.product_sku)
        )) || visualizations[0]
    const machineSection = machineProduct?.ListSections?.find(
        (visualizationSection: MachineVisualizationSection) => (
            visualizationSection?.SectionName === section.name
        )
    )

    return {machineProduct, machineSection}
}

const OrderReview = () => {
    const {id} = useParams()
    const searchParams = useSearchParams();
    const id_type = searchParams.get('idType');
    const [order, setOrder] = useState<SingleOrder>()
    const [quantity, setQuantity] = useState(0)
    const [lineItems, setLineItems] = useState<SingleOrderDetail[]>([]);
    const [orderDetail, setOrderDetail] = useState<SingleOrderDetail>();
    const [is3DModalOpen, setIs3DModalOpen] = useState(false);
    const [isProgressHistoryModalOpen, setIsProgressHistoryModalOpen] = useState(false);
    const [isOrderHistoryModalOpen, setIsOrderHistoryModalOpen] = useState(false);
    const [progressHistoryLineItem, setProgressHistoryLineItem] = useState<SingleOrderDetail | null>(null);
    const [loadingVisualization, setLoadingVisualization] = useState(false);
    const [selectedFormats, setSelectedFormats] = useState<{ [sectionId: string]: string[] }>({});
    const [downloadingDesignId, setDownloadingDesignId] = useState<string | null>(null);
    const [useProfanity, setUseProfanity] = useState<boolean>(true);
    const router = useRouter();


    const {companySetting} = useSnapshot(store);

    useEffect(() => {
        loadOrder();
        getSettings().then((settings: any[]) => {
            const setting = settings?.find((item: any) => item.key === 'use_profantity')
            if (setting && setting.value === false) {
                setUseProfanity(false)
            }
        }).catch(() => {})
    }, [id, id_type])

    // Backstop for the hard-refresh race: companySetting.machine is populated asynchronously by
    // useCurrentUser, so loadOrder's visualization fetch is sometimes skipped on a fresh page load
    // (machine still null at that moment). Re-run it once the machine setting arrives and the order
    // is loaded — this is why a manual refresh previously never showed the machine-generated output.
    useEffect(() => {
        if (companySetting?.machine === 'neatolabs' && order) {
            loadVisualizationsFor(order)
        }
    }, [companySetting?.machine])

    const redirectToProduct = (sku: string) => {
        const currentUrl = typeof window !== "undefined" ? new URL(window.location.href) : "";
        const queryParams = currentUrl.search;
        let newUrl = `/products/${sku}${queryParams}`;
        if (companySetting?.profile_version === 'v2') {
					newUrl = `/products/v2/${sku}${queryParams}`;
        }
				
        window.open(newUrl, '_blank');
    };
    const handleCopyStoreOrder = async () => {
        try {
            await navigator.clipboard.writeText(order?.external_order_number || '')
            toast.success('Store order number copied')
        } catch {
            notifyError('Unable to copy store order number')
        }
    }

    const qrContainerRef = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = useState(false);
   // Print the QR label through the standard OS print dialog. Renders the QR + order number
   // into an isolated print window so the operator can pick whichever printer they want,
   // rather than relaying to a hardcoded printer IP.
   const handlePrint = () => {
    try {
        setIsPrinting(true);

        const orderNumber = order?.order_number;
        if (!orderNumber) {
            throw new Error('Order number is missing.');
        }

        const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(orderNumber)}`;

        const printWindow = window.open('', '_blank', 'width=400,height=500');
        if (!printWindow) {
            throw new Error('Unable to open the print window. Please allow pop-ups for this site.');
        }

        printWindow.document.write(`
            <html>
                <head>
                    <title>Order ${orderNumber}</title>
                    <style>
                        @page { margin: 0; }
                        html, body { margin: 0; padding: 0; }
                        .label {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            padding: 12px;
                            font-family: sans-serif;
                        }
                        .label img { width: 170px; height: 170px; }
                        .label .order-number {
                            margin-top: 6px;
                            font-size: 14px;
                            color: #000;
                            text-align: center;
                        }
                    </style>
                </head>
                <body>
                    <div class="label">
                        <img src="${qrSrc}" alt="QR Code for Order ${orderNumber}" />
                        <div class="order-number">${orderNumber}</div>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();

        const triggerPrint = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
            setIsPrinting(false);
        };

        const qrImage = printWindow.document.querySelector('img');
        if (qrImage && !qrImage.complete) {
            qrImage.onload = triggerPrint;
            qrImage.onerror = triggerPrint;
        } else {
            triggerPrint();
        }
    } catch (err: any) {
        console.error('Print Error details:', err.message);
        notifyError(err, 'Unable to print the label.');
        setIsPrinting(false);
    }
};

    const fetchVisualizationData = async (jobs: OrderJob[]) => {
        let visualizationDataList: Array<object> = [];
        await Promise.allSettled(jobs.map(async (item) => {
            const data = await getVisualization(item.visualization_path);
            // The visualization JSON can 404 briefly right after a job is created (file not
            // written yet) — getVisualization returns null in that case. Skip instead of throwing.
            if (!data) return;
            data.bundle_index = item.bundle_index;
            data.sku = item.sku;
            visualizationDataList.push(data);

        }));
        return visualizationDataList;
    };

    // Fetch machine-generated visualization data for an order's line items and merge it into
    // both `order` (which the render reads via order.order_details) and `lineItems`. neatolabs only.
    // Right after a job is created (reprocess / profanity approval) the visualization JSON may not
    // be written yet, so each item is retried with backoff until it resolves or attempts run out.
    const loadVisualizationsFor = async (orderObj: SingleOrder) => {
        // Read the live store value (not the render snapshot) so a hard-refresh race — where
        // companySetting is populated asynchronously after this component mounts — doesn't cause
        // the fetch to be skipped.
        if (store.companySetting?.machine !== 'neatolabs') return;
        const details = orderObj?.order_details;
        if (!details || details.length === 0) return;

        setLoadingVisualization(true);

        const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));
        const MAX_ATTEMPTS = 4;

        const updated: SingleOrderDetail[] = await Promise.all(
            details.map(async (item) => {
                if (!item.jobs || item.jobs.length === 0) return item;
                for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
                    const viz = await fetchVisualizationData(item.jobs);
                    if (viz && viz.length > 0) {
                        return { ...item, visualization: viz };
                    }
                    // Not ready yet — wait a bit and retry (1s, 2s, 3s).
                    if (attempt < MAX_ATTEMPTS - 1) await sleep(1000 * (attempt + 1));
                }
                return item;
            })
        );

        // Update `order` immutably so the render (which maps order.order_details) reliably reflects
        // the new visualization, and keep `lineItems` in sync without clobbering selection state.
        setOrder(prev => (prev ? { ...prev, order_details: updated } : { ...orderObj, order_details: updated }));
        setLineItems(prev => prev.map(li => {
            const u = updated.find(x => x.id === li.id);
            return u?.visualization ? { ...li, visualization: u.visualization } : li;
        }));

        setLoadingVisualization(false);
    };

    const loadOrder = async () => {
        const result = await getOrder(id,id_type ?? undefined);
        setQuantity(0);

        if (result) {
            const order_details = result.order.order_details;


            // Get unique SKUs from order details
            //const uniqueSkus = [...new Set(order_details.map((item: SingleOrderDetail) => item.sku))];
            const uniqueSkus = [...new Set(order_details.map((item: SingleOrderDetail) => item.sku))].filter((sku): sku is string => typeof sku === 'string');


            // Fetch product details for unique SKUs
            const productDetailsMap = new Map();
            const productPromises = uniqueSkus.map(async (sku) => {
                if (!sku) return;
                const productDetails = await getProduct(sku);
                productDetailsMap.set(sku, productDetails);
            });
            await Promise.all(productPromises);


            const promises = order_details.map(async (item: SingleOrderDetail) => {
                setQuantity(prev => prev + item.quantity);
                const order_details_result = productDetailsMap.get(item.sku);
                item.image_url = order_details_result ? order_details_result['2d_image_url'] : "";
                item.is_bundle = order_details_result ? order_details_result['is_bundle'] : false;
                item.name = order_details_result ? order_details_result.name : '';
                item.variant_id = order_details_result ? order_details_result.variant_id : '';
                item.product = order_details_result
                return item;
            });

            const processedItems: SingleOrderDetail[] = await Promise.all(promises);
            setLineItems(processedItems);
            setOrder(result.order);

            // Fetch machine-generated visualization (neatolabs only), with retry for files that
            // aren't written yet right after a reprocess / profanity-approval creates the job.
            await loadVisualizationsFor(result.order);
        }
    };

    if (!order) {
        return null
    }

    const selectedLineItems = lineItems.filter(item => item.checked)
    const isRejectButtonDisabled = selectedLineItems.some(isLineItemProfanityRejected)
    const unapprovedSelectedItems = selectedLineItems.filter(item => !isLineItemProfanityApproved(item))

    const handleOnApproveOrRejectLineItems = async (status: OrderProfanityCheckStatus) => {
        if (status === OrderProfanityCheckStatus.REJECTED && isRejectButtonDisabled) {
            return
        }

        const ids = lineItems.filter(item => item.checked).reduce((accumulator: IdsObject, detail) => {
            accumulator[detail.id] = {status: status};
            return accumulator;
        }, {});
        const lineItemsReq = {ids: ids}
        await updateOrderProfanityCheckLineItems(order?.id, lineItemsReq);
        if (status === OrderProfanityCheckStatus.ACCEPTED) {
            store.loadingPage = true
            const getJobResult = companySetting?.machine === 'neatolabs' ? getResultReprocessJobLineItems : getResultImageServiceJob
            const interval = setInterval(async () => {
                const data = await getJobResult(order?.id)
                if (!data) return
                if (data.is_successful) {
                    store.loadingPage = false
                    clearInterval(interval)
                    loadOrder()
                } else if (!data.is_running && !data.is_successful) {
                    store.loadingPage = false
                    toast.error(buildJobFailureMessage(data))
                    clearInterval(interval)
                } else if (data.is_canceled) {
                    store.loadingPage = false
                    notifyError('Job Generation failed!')
                    clearInterval(interval)
                }
            }, 5000)
        } else {
            loadOrder()
        }
    }

    const handleOnReprocessJob = async () => {
        await reprocessJob(order?.id);
        loadOrder();
    }

    const handleOnReprocessItemsJob = async () => {
        // Reprocess is per line item, so it is the selection that has to be approved --
        // there is no order-wide verdict to check. Tell the operator which items are
        // holding it up instead of hiding the button.
        if (unapprovedSelectedItems.length > 0) {
            const names = unapprovedSelectedItems.map(item => item.sku).join(', ')
            notifyError(
                `Approve these line items before reprocessing: ${names}.`,
            )
            return
        }

        store.loadingPage = true;
        const lineItemIds = lineItems.filter(item => item.checked).map(item => item.id);
        const lineItemsReq = {ids: lineItemIds}
        await reprocessJobLineItems(order?.id, lineItemsReq);
        const getJobResult = companySetting?.machine === 'neatolabs' ? getResultReprocessJobLineItems : getResultImageServiceJob
        const interval = setInterval(async () => {
            const data = await getJobResult(order?.id);
            if (!data) return;
            if (data.is_successful) {
                store.loadingPage = false;
                clearInterval(interval)
                loadOrder();
            } else if (!data.is_running && !data.is_successful) {
                store.loadingPage = false;
                toast.error(buildJobFailureMessage(data));
                clearInterval(interval)
            } else if (data.is_canceled) {
                store.loadingPage = false;
                notifyError(`Job Generation failed!`);
                clearInterval(interval)
            }
        }, 5000)

    }

     const handleMarkAsDone = async () => {
         await markOrderAsDone(order?.id);
        loadOrder();
    }

    const handleSelectAll = (checked: boolean) => {
        setLineItems(prevState => prevState.map(item => ({
            ...item,
            checked: checked
        })));
    }
    const handleSelectLineItem = (checked: boolean, id: string) => {
        setLineItems(prevState => {
            return prevState.map(item => {
                if (item.id === id) {
                    return {
                        ...item,
                        checked: checked
                    };
                }
                return item;
            });
        });
    }

    const handleLineItemQuantityUpdated = (lineItemId: string, nextQuantity: number) => {
        const previousQuantity = order.order_details.find(item => item.id === lineItemId)?.quantity
        if (previousQuantity === undefined || nextQuantity <= previousQuantity) return

        const updateQuantity = (item: SingleOrderDetail) => (
            item.id === lineItemId ? {...item, quantity: nextQuantity} : item
        )

        setOrder(currentOrder => currentOrder
            ? {...currentOrder, order_details: currentOrder.order_details.map(updateQuantity)}
            : currentOrder
        )
        setLineItems(currentItems => currentItems.map(updateQuantity))
        setOrderDetail(currentItem => currentItem?.id === lineItemId
            ? {...currentItem, quantity: nextQuantity}
            : currentItem
        )
        setProgressHistoryLineItem(currentItem => currentItem?.id === lineItemId
            ? {...currentItem, quantity: nextQuantity}
            : currentItem
        )
        setQuantity(currentTotal => currentTotal + (nextQuantity - previousQuantity))

        // Refresh the order from the server so status, processed counts, etc. reflect the change.
        loadOrder()
    }

    const scrollToNextElement = () => {
        const lineItem = lineItems.find(item => !item.checked)
        lineItem && router.push(`#${lineItem?.id}`);
    };

    const getProfanityResult = (result: { rsp: {} }) => {
       const profanityItems = ['alcohol', 'drugs', 'gore', 'nudity', 'nuditypartial', 'offensive', 'weapon'];
    let arr: Array<object> = [];

    Object.entries(result?.rsp ? result?.rsp : result).map(([key, value]) => {
        if (profanityItems.some((item) => item === key.toLowerCase())) {
            const profanityResult = {
                "key": key.charAt(0).toUpperCase() + key.slice(1),
                'value': value,
                'class': Number(value) >= 70 ? 'bg-[#FEE4E2] text-[#D92D20]' : Number(value) >= 30 ? 'bg-[#FEF0C7] text-[#B54708]' : 'bg-[#FAFAFA] text-[#4E554C]'
            };
            arr.push(profanityResult);
        }
    });
    return arr;
};

    const getAvailableFormats = (section: any) => {
        const artworkUrls = section.artwork_urls || {};
        const formats: { key: string; label: string; url: string }[] = [];

        Object.entries(artworkUrls).forEach(([key, value]) => {
            if (key.endsWith('_url') && typeof value === 'string' && value) {
                const format = key.replace('_url', '').toUpperCase();
                formats.push({ key, label: format, url: value as string });
            }
        });

        return formats;
    };

    const handleFormatToggle = (sectionId: string, formatKey: string) => {
        setSelectedFormats(prev => {
            const current = prev[sectionId] || [];
            if (current.includes(formatKey)) {
                return { ...prev, [sectionId]: current.filter(f => f !== formatKey) };
            } else {
                return { ...prev, [sectionId]: [...current, formatKey] };
            }
        });
    };

    const handleSectionToggle = (sectionId: string, formats: { key: string }[]) => {
        const allKeys = formats.map(f => f.key);
        const current = selectedFormats[sectionId] || [];

        if (current.length > 0) {
            setSelectedFormats(prev => ({ ...prev, [sectionId]: [] }));
        } else {
            setSelectedFormats(prev => ({ ...prev, [sectionId]: allKeys }));
        }
    };

    const handleDownloadSelectedDesigns = async (
        lineItemId: string,
        sectionDownloads: Array<{ section: any; sectionKey: string }>
    ) => {
        const downloads = sectionDownloads
            .map(({section, sectionKey}) => ({
                section,
                selectedFormatKeys: selectedFormats[sectionKey] || [],
            }))
            .filter(({selectedFormatKeys}) => selectedFormatKeys.length > 0);

        if (downloads.length === 0) {
            notifyError('Please select at least one section and file format to download');
            return;
        }

        setDownloadingDesignId(lineItemId);

        try {
            const zip = new JSZip();

            for (const [index, {section, selectedFormatKeys}] of downloads.entries()) {
                const fileName = `Order_${order.order_number}__SKU_${section.product_sku}__Section_${section.name}`;
                const folder = zip.folder(`${String(index + 1).padStart(2, '0')}__${fileName}`);
                folder?.file(`${fileName}.json`, JSON.stringify(section, null, 2));

                const artworkUrls = section.artwork_urls || {};
                for (const formatKey of selectedFormatKeys) {
                    const url = artworkUrls[formatKey];
                    if (url && typeof url === 'string') {
                        try {
                            const extension = formatKey.replace('_url', '');
                            const imageBlob = await fetch(url).then(res => res.blob());
                            folder?.file(`${fileName}.${extension}`, imageBlob);
                        } catch (error) {
                            console.error(`Error fetching ${formatKey}:`, error);
                        }
                    }
                }
            }

            const content = await zip.generateAsync({ type: 'blob' });

            const now = new Date();
            const version = now.getFullYear() + '_' +
                String(now.getMonth() + 1).padStart(2, '0') + '_' +
                String(now.getDate()).padStart(2, '0') + '_' +
                String(now.getHours()).padStart(2, '0') + '_' +
                String(now.getMinutes()).padStart(2, '0') + '_' +
                String(now.getSeconds()).padStart(2, '0');

            saveAs(content, `Order_${order.order_number}__Designs__v_${version}.zip`);
        } finally {
            setDownloadingDesignId(null);
        }
    }

    const getSectionDownloadKey = (orderDetailId: string, section: any, sectionIndex: number) =>
        `${orderDetailId}_${section.product_sku}_${section.bundle_index}_${section.name}_${sectionIndex}`;

    const renderDownloadDesignButton = (lineItem: SingleOrderDetail) => {
        const sections = lineItem.sections || [];
        const sectionDownloads = sections.map((section, sectionIndex) => {
            const sectionKey = getSectionDownloadKey(lineItem.id, section, sectionIndex);
            const availableFormats = getAvailableFormats(section);
            const artworkStatus = String((section as any)?.artwork_urls?.status || '').toLowerCase();
            const isArtworkReady = !artworkStatus || ['success', 'approved', 'ready', 'completed'].includes(artworkStatus);
            return {
                section,
                sectionKey,
                availableFormats,
                isAvailable: isArtworkReady && availableFormats.length > 0,
                selectedCount: (selectedFormats[sectionKey] || []).length,
            };
        });
        const hasAvailableDesign = sectionDownloads.some(item => item.isAvailable);
        const selectedCount = sectionDownloads.reduce((total, item) => total + item.selectedCount, 0);
        const selectedSectionCount = sectionDownloads.filter(item => item.selectedCount > 0).length;
        const isDownloading = downloadingDesignId === lineItem.id;

        return (
            <Popover
                placement={['bottom right', 'top right', 'bottom left', 'top left']}
                offsetX={0}
                offsetY={10}
                toggle={() => (
                    <span
                        className="inline-flex h-full"
                        title={hasAvailableDesign ? undefined : 'Pending approval'}
                    >
                        <Button
                            type={'alt'}
                            className="self-stretch !h-[60px]"
                            disabled={!hasAvailableDesign}
                        >
                            <span className="flex items-center gap-xs">
                                <span className="font-icon-solid"></span>
                                <span>{`Download Design${selectedCount > 0 ? ` (${selectedCount})` : ''}`}</span>
                            </span>
                        </Button>
                    </span>
                )}
                content={(close) => (
                    <div className="flex w-[340px] max-w-[calc(100vw-32px)] max-h-[70vh] flex-col overflow-hidden">
                        <div className="overflow-y-auto">
                            {sectionDownloads.map(({section, sectionKey, availableFormats, isAvailable, selectedCount: sectionSelectedCount}) => (
                                <div key={sectionKey} className="p-3 border-b border-gray-100 last:border-b-0">
                                    {isAvailable ? (
                                        <>
                                            <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                                                <input
                                                    type="checkbox"
                                                    checked={sectionSelectedCount > 0}
                                                    onChange={() => handleSectionToggle(sectionKey, availableFormats)}
                                                    className="w-4 h-4"
                                                />
                                                <span className="text-sm font-semibold text-gray-900">{section.name}</span>
                                            </label>
                                            <div className="mt-1 ml-6">
                                                {availableFormats.map(format => (
                                                    <label key={format.key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                                                        <input
                                                            type="checkbox"
                                                            checked={(selectedFormats[sectionKey] || []).includes(format.key)}
                                                            onChange={() => handleFormatToggle(sectionKey, format.key)}
                                                            className="w-4 h-4"
                                                        />
                                                        <span className="text-sm">{format.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="flex items-center justify-between gap-3 p-1">
                                            <p className="text-sm font-semibold text-gray-400">{section.name}</p>
                                            <span className="text-xs text-gray-400">Pending approval</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="shrink-0 border-t border-gray-200 bg-white p-3">
                            <Button
                                onClick={async () => {
                                    await handleDownloadSelectedDesigns(lineItem.id, sectionDownloads);
                                    close();
                                }}
                                type={'primary'}
                                className="w-full"
                                disabled={selectedCount === 0 || isDownloading}
                            >
                                <span className="flex items-center gap-xs">
                                    {isDownloading
                                        ? <Loader2 size={17} className="animate-spin"/>
                                        : <span className="font-icon-solid"></span>
                                    }
                                    <span>
                                        {selectedCount > 0
                                            ? `Download Design (${selectedSectionCount} section${selectedSectionCount === 1 ? '' : 's'}, ${selectedCount} format${selectedCount === 1 ? '' : 's'})`
                                            : 'Download Design'
                                        }
                                    </span>
                                </span>
                            </Button>
                        </div>
                    </div>
                )}
            />
        );
    };


    return (
        <div className="flex w-full bg-white gap-[32px]">
            <Modal
                isOpen={is3DModalOpen}
                onClose={() => setIs3DModalOpen(false)}
                title={"Edit Order"}
                className={"!w-[calc(100vw-70px)] h-[calc(90vh-70px)] !p-0"}
                containerClassName={"!py-[0px]"}
                windowClassName={"h-[100vh]"}
            >
                {orderDetail &&
                    <Order3DPlugin orderDetail={orderDetail} setIs3DModalOpen={setIs3DModalOpen} loadOrder={loadOrder}/>
                }
            </Modal>
            <LineItemProgressHistoryDialog
                isOpen={isProgressHistoryModalOpen}
                orderId={order.id}
                lineItem={progressHistoryLineItem}
                onClose={() => setIsProgressHistoryModalOpen(false)}
                onQuantityUpdated={handleLineItemQuantityUpdated}
            />
            <OrderHistoryDialog
                isOpen={isOrderHistoryModalOpen}
                orderId={order.id}
                orderNumber={order.order_number}
                onClose={() => setIsOrderHistoryModalOpen(false)}
            />
            <div className="flex-grow">
                <Heading
                    title={`# ${order.order_number}`}
                    description={
                        <div className="absolute right-0 top-0 m-lg mr-xxl mt-xl">
                            <button
                                onClick={handlePrint}
                                disabled={isPrinting}
                                className="absolute -left-10 top-0 rounded-full bg-white p-2 shadow-md transition-colors hover:text-blue-600"
                                title="Print Label"
                            >
                                {isPrinting ? <Loader2 className="animate-spin" size={20}/> : <Printer size={20}/>}
                            </button>
                            <div ref={qrContainerRef} className="ml-5 bg-transparent p-[5px]">
                                <img
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=170x170&data=${order.order_number}`}
                                    alt={`QR Code for Order ${order.order_number}`}
                                />
                                <div className="mt-[5px] text-center text-xs text-black">
                                    {order.order_number}
                                </div>
                            </div>
                        </div>
                    }
                    parent={{
                        title: 'Orders',
                        url: '/orders',
                    }}
                    contentClassName="min-w-0 flex-1"
                    childrenClassName="pr-[216px]"
                >
                    <div className="flex flex-row flex-wrap items-stretch gap-sm">
                        <OrderHeaderBadge title="Store Order">
                            <div className="flex items-center gap-sm">
                                <span>#{order.external_order_number}</span>
                                <button
                                    type="button"
                                    className="text-gray-400 transition-colors hover:text-primary-600"
                                    title="Copy Store Order"
                                    onClick={handleCopyStoreOrder}
                                >
                                    <Copy size={14}/>
                                </button>
                            </div>
                        </OrderHeaderBadge>
                        <OrderHeaderBadge title="Order Status">
                            {getStatus(order.order_status)}
                        </OrderHeaderBadge>
                        <OrderHeaderBadge title={`${order.order_details.length} ${order.order_details.length === 1 ? 'Item' : 'Items'}`}>
                            Total Qty: {quantity}
                        </OrderHeaderBadge>
                        <OrderHeaderBadge title="Placed On">
                            {formatDateFromStringWithHour(order.order_date)}
                        </OrderHeaderBadge>
                        <OrderHeaderBadge title="Received On">
                            {formatDateFromStringWithHour(order.created_at)}
                        </OrderHeaderBadge>
                        <Button
                            type="alt"
                            className="self-stretch !h-auto !px-4 !py-2"
                            icon={<Clock size={18}/>}
                            onClick={() => setIsOrderHistoryModalOpen(true)}
                        >
                            Order History
                        </Button>
                    </div>
                </Heading>


                <div className='flex w-full flex-col justify-between overflow-hidden px-[109px]'>
                    <div className="text-gray-900 text-title-xl">Line Items - <div
                        className="text-text-md text-gray-400 gap-sm inline-flex items-center mb-5"> Review and evaluate
                        each image and snapshot for approval or rejection.
                    </div></div>
                </div>
                <div className='flex w-full flex-col justify-between px-[109px]'>
                    <div className='w-full px-4 sticky top-0 bg-white z-10 shadow-[0_3px_2px_-2px_rgba(0,0,0,.1)]'>
                        <div className='flex flex-row items-center'>

                            <Checkbox
                                label={'Select All'}
                                className={(select) => (select ? ' h-[38px] pr-md rounded-md ' : 'pr-md h-[38px]')}
                                onChange={handleSelectAll}
                                defaultChecked={lineItems.every((it: SingleOrderDetail) => it.checked)}
                            />
                        </div>
                    </div>
                    {
                         order.order_details.sort((a, b) => b.created_at.localeCompare(a.created_at)).map((orderDetail) => {
                             const profanityStatus = String(orderDetail.profanity_check_status || '').trim().toLowerCase();
                             const isPendingApproval = ![
                                 OrderProfanityCheckStatus.ACCEPTED,
                                 OrderProfanityCheckStatus.REJECTED,
                             ].includes(profanityStatus as OrderProfanityCheckStatus);
                             const isProfanityRejected = profanityStatus === OrderProfanityCheckStatus.REJECTED;
                             // Copy before sorting — Array.sort mutates, and sorting the order's own
                             // section array during render reorders the state these rows read from.
                             const sortedSections = [...(orderDetail.sections ?? [])]
                                 .sort((a, b) => a.radial_angle - b.radial_angle)
                                 .sort((a, b) => a.bundle_index - b.bundle_index);
                             const sectionPageNumbers = getSectionPageNumbers(sortedSections);
                             return (
                                 <div key={orderDetail.id} className='w-full' id={`${orderDetail.id}`}>
                                     <div key={orderDetail.id} className='w-full bg-gray-100 my-5 rounded-md p-4 sticky top-[47px] z-[9]'>
                                         <div className='flex flex-row items-center'>
                                             <Checkbox
                                                 label={''}
                                                 defaultChecked={lineItems.some((it: SingleOrderDetail) => it.id === orderDetail.id && it.checked)}
                                                 className={(select) => (select ? ' h-[38px] pr-md rounded-md ' : 'pr-md h-[38px]')}
                                                 onChange={(e) => handleSelectLineItem(e, orderDetail.id)}
                                             />
                                             <ImageComponent
                                                 src={orderDetail.image_url}
                                                 className='mr-5 !h-[100px] !w-[100px] shrink-0 !object-contain'
                                                 style={{width: '100px', height: '100px', objectFit: 'contain'}}
                                             />
                                             <div>
                                                 <div
                                                     className="mb-4 flex flex-row items-center pl-4 text-start text-title-xl text-gray-900">
                                                     <span className='mr-2.5'>{orderDetail.name}</span>
                                                 </div>

                                                 <div className='flex flex-row justify-between'>
                                                     <div className='flex flex-row flex-wrap gap-sm'>
                                                         <div
                                                             className='flex col justify-between bg-white py-2 pl-4 pr-10 rounded-md'>
                                                             <div className={'flex flex-col gap-sm'}>
                                                                 <p className='text-gray-400 text-xs'>
                                                                     SKU
                                                                 </p>
                                                                 <p className='text-gray-700 text-xs'>
                                                                     <a onClick={() => redirectToProduct(orderDetail.sku)}
                                                                        className='cursor-pointer'>
                                                                         {orderDetail.sku}
                                                                     </a>
                                                                 </p>
                                                             </div>
                                                         </div>

                                                         <div
                                                             className='flex col justify-between bg-white py-2 pl-4 pr-10 rounded-md'>
                                                             <div className={'flex flex-col gap-sm'}>
                                                                 <p className='text-gray-400 text-xs'>
                                                                     {formatDateFromStringWithHour(orderDetail.created_at)}
                                                                 </p>
                                                                 <p className='text-gray-700 text-xs'>
                                                                     {getStatus(orderDetail.status)}
                                                                 </p>
                                                             </div>
                                                         </div>

                                                         {companySetting?.machine === 'neatolabs' && (
                                                             <div
                                                                 className='flex col justify-between bg-white py-2 pl-4 pr-10 rounded-md'>
                                                                 <div className={'flex flex-col gap-sm'}>
                                                                     <p className='text-gray-400 text-xs'>
                                                                         Job Status
                                                                     </p>
                                                                     <p className='text-gray-700 text-xs'>
                                                                         {getStatus(orderDetail?.lumialase_job_call_status)}
                                                                     </p>
                                                                 </div>
                                                             </div>
                                                         )}

                                                         <div
                                                             className={`flex col justify-between rounded-md py-2 pl-4 pr-10 ${
                                                                 isProfanityRejected
                                                                     ? 'border border-red-300 bg-red-50'
                                                                     : isPendingApproval
                                                                         ? 'border border-[#FEC84B] bg-[#FEF0C7]'
                                                                         : 'bg-white'
                                                             }`}>
                                                             <div className={'flex flex-col gap-sm'}>
                                                                 <p className={`text-xs ${
                                                                     isProfanityRejected
                                                                         ? 'text-red-700'
                                                                         : isPendingApproval
                                                                             ? 'text-[#B54708]'
                                                                             : 'text-gray-400'
                                                                 }`}>
                                                                     Profanity
                                                                 </p>
                                                                 <p className={`text-xs ${
                                                                     isProfanityRejected
                                                                         ? 'font-medium text-red-700'
                                                                         : isPendingApproval
                                                                             ? 'font-medium text-[#B54708]'
                                                                             : 'text-gray-700'
                                                                 }`}>
                                                                     {isPendingApproval ? 'Pending Approval' : `${getStatus(orderDetail.profanity_check_status)}`}
                                                                 </p>
                                                             </div>
                                                         </div>

                                                         <Button
                                                             type="alt"
                                                             className="self-stretch !h-auto !w-auto shrink-0 !px-4 !py-2"
                                                             onClick={() => {
                                                                 setProgressHistoryLineItem(orderDetail)
                                                                 setIsProgressHistoryModalOpen(true)
                                                             }}
                                                         >
                                                             <span className="flex flex-col items-start gap-sm text-left">
                                                                 <span className="text-xs text-primary-700">Quantity & History</span>
                                                                 <span className="whitespace-nowrap text-xs font-medium text-gray-700">
                                                                     <span className="text-[#027A48]">{orderDetail.quantity_done}</span>
                                                                     {` from ${orderDetail.quantity} `}
                                                                     <span className={orderDetail.quantity_failed > 0 ? 'text-[#D92D20]' : 'text-gray-400'}>
                                                                         {`(${orderDetail.quantity_failed} Failed Attempt${orderDetail.quantity_failed === 1 ? '' : 's'})`}
                                                                     </span>
                                                                 </span>
                                                             </span>
                                                         </Button>
                                                         <Button
                                                             type={'alt'}
                                                             className={'self-stretch !w-auto !min-w-[125px] !h-auto shrink-0'}
                                                             onClick={() => {
                                                                 setIs3DModalOpen(true);
                                                                 setOrderDetail(orderDetail)
                                                             }}
                                                         >
                                                             <span className="flex items-center gap-xs">
                                                                 <span className={'font-icon-solid'}></span>
                                                                 <span>Edit Design</span>
                                                             </span>
                                                         </Button>
                                                         {renderDownloadDesignButton(orderDetail)}
                                                     </div>
                                                 </div>
                                             </div>
                                         </div>

                                     </div>
                                     <div>
                                         {
                                             sortedSections.map((sectionDetail, index) => {
                                                 const {machineProduct, machineSection} =
                                                     getMachineVisualizationForSection(orderDetail, sectionDetail)

                                                 return (
                                                     <div key={sectionDetail.section_artwork_url + index}
                                                          className='bg-white py-3 rounded flex flex-row justify-start gap-xl border-gray-200 border-b'>
                                                         <SectionImageComparison
                                                             section={sectionDetail}
                                                             machineSection={machineSection}
                                                             machineProduct={machineProduct}
                                                             showMachine={companySetting?.machine === 'neatolabs' && !isProfanityRejected}
                                                             isMachineLoading={loadingVisualization}
                                                             machineStatusMessage={
                                                                 orderDetail?.lumialase_job_call_status === 'not_started'
                                                                     ? "- Job hasn't been generated!"
                                                                     : ''
                                                             }
                                                         />
                                                         <div className='min-w-0 flex-1'>
                                                             <p className='mb-4 text-base font-medium'>Raw Design Images</p>
                                                             <div>
                                                                 {
                                                                     sectionDetail.raw_images.map((rawImage, idx) => {
                                                                     const element = sectionDetail?.elements && sectionDetail?.elements[idx];
                                                                     const src = element
                                                                         ? (element.type === 'custom' && element.custom?.rawImage) ||
                                                                         (element[element.type]?.list && element[element.type].list[0]?.image) ||
                                                                         rawImage.element_raw_image
                                                                         : rawImage.element_raw_image;
                                                                     return <div key={rawImage.element_raw_image}>
                                                                         <div className='flex w-full flex-row gap-xl'>
                                                                             <DesignElementImage
                                                                                 src={src}
                                                                                 page={sectionPageNumbers[index]}
                                                                                 alt={`Design element ${idx + 1}`}
                                                                             />

                                                                             <div className='mt-12 flex min-w-0 flex-1 flex-col'>

                                                                                 <div
                                                                                     key={rawImage.element_raw_image + index}
                                                                                     className='flex w-full flex-wrap gap-2'>
                                                                                     {rawImage?.profanity_check_result &&
                                                                                         <>
                                                                                             {getProfanityResult(rawImage?.profanity_check_result)?.map((item: any, index) => (
                                                                                                 <span key={index}
                                                                                                       className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium h-[32px] ${item.class}`}>{`${item.value}% ${item.key} Related Content`}</span>
                                                                                             ))}
                                                                                         </>
                                                                                     }
                                                                                 </div>

                                                                             </div>
                                                                         </div>
                                                                         {idx < sectionDetail.raw_images.length - 1 &&
                                                                             <div className='my-xl border-t border-gray-200' />
                                                                         }
                                                                     </div>
                                                                     })
                                                                 }
                                                             </div>
                                                         </div>

                                                     </div>
                                                 )
                                             })
                                         }
                                     </div>

                                 </div>
                             )
                        })
                    }
                </div>

                <div className='h-[100px]'></div>
                {lineItems.some(item => item.checked) &&
                    <div className="fixed inset-x-0 bottom-0 w-full h-32 px-0 pt-[10px] pb-xl z-50 bg-primary-500/70 backdrop-blur-[10px] border-t border-gray-300 dark:bg-gray-700/70 dark:border-gray-600">
                        <div className='flex w-full flex-col gap-sm px-[109px]'>
                            <p className="text-sm font-semibold text-white [text-shadow:0_1px_1px_rgba(0,0,0,0.35)]">
                                {`${lineItems.filter(item => item.checked).length} Item${lineItems.filter(item => item.checked).length === 1 ? '' : 's'} selected`}
                            </p>
                            <div className='grid grid-flow-col gap-3'>
                            {useProfanity &&
                                <Button
                                    size={'lg'}
                                    onClick={() => handleOnApproveOrRejectLineItems(OrderProfanityCheckStatus.REJECTED)}
                                    type={'danger'}
                                    icon={<XCircle size={18}/>}
                                    className="mr-1"
                                    disabled={isRejectButtonDisabled}
                                    title={isRejectButtonDisabled ? 'Already rejected.' : undefined}
                                >
                                    {isRejectButtonDisabled ? 'Already Rejected' : 'Reject'}
                                </Button>
                            }
                            <Button
                                size={'lg'}
                                onClick={handleOnReprocessItemsJob}
                                type={'alt'}
                                icon={<RefreshCw size={18}/>}
                                className="mr-1 !border-amber-300 !bg-amber-50 !text-amber-700 hover:!bg-amber-100 focus:!border-amber-400"
                            >
                                Reprocess Job
                            </Button>
                            {!companySetting?.machine &&
                                <Button
                                    size={'lg'}
                                    onClick={handleMarkAsDone}
                                    type={'primary'}
                                    icon={<CheckCheck size={18}/>}
                                    className="mr-1"
                                >
                                    Mark as done
                                </Button>
                            }
                            {useProfanity && <>
                                <Button
                                    size={'lg'}
                                    onClick={() => handleOnApproveOrRejectLineItems(OrderProfanityCheckStatus.ACCEPTED)}
                                    type={'alt'}
                                    icon={<CheckCircle2 size={18}/>}
                                >
                                    Approve
                                </Button>
                            </>}
                            </div>
                        </div>
                </div>
                }
            </div>
        </div>
    )
}

export default OrderReview
